function defaultRequestId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  return `dashboard-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * The note where `value` is an addition rather than the field.
 *
 * Kept in step with APPEND_ONLY_NOTE_FIELDS in
 * functions/src/container-field-policy.ts, which is where the rule is enforced.
 * This list decides what the screen does while the save is in flight, and which
 * note editors open empty. Hold left it on 2026-09-25 (Alan: "和普通text box 一样").
 */
export const APPEND_ONLY_NOTE_FIELDS = new Set(['customer_note']);

const DATE_FIELDS = new Set([
  'eta_at', 'lfd_at', 'pickup_appointment_at', 'transfer_at', 'reported_empty_at',
  'return_empty_lfd_at', 'empty_returned_at', 'special_fee_date',
  'special_fee_2_date', 'special_fee_3_date',
]);

// Kept byte-for-byte in step with DASHBOARD_EXPENSE_FIELDS /
// DASHBOARD_REVENUE_FIELDS in functions/src/dashboard-finance-totals.ts, which is
// where the rule actually lives. Transfer Cost, Pier Pass and Exception Fee were
// missing here, so the optimistic Total Expense disagreed with the server's
// (Alan, 2026-08-02: red is all eight, added).
const TOTAL_EXPENSE_FIELDS = Object.freeze([
  'pickup_cost_cents', 'return_cost_cents', 'transfer_cost_cents',
  'special_fee_amount_cents', 'special_fee_2_amount_cents', 'special_fee_3_amount_cents',
]);

const TOTAL_REVENUE_FIELDS = Object.freeze([
  'customer_receivable_cents', 'service_fee_cents', 'customer_special_fee_cents',
]);

// Mirrors pierPassCentsForFees in functions/src/pier-pass-fees.ts. CTF $20 and TMF $81.26, each counted
// once its chip is Paid - Unpaid and Customer Paid count nothing (Alan, 2026-10-10).
const CTF_FEE_CENTS = 2000;
const TMF_FEE_CENTS = 8126;

function pierPassCents(row) {
  const paid = (status) => String(status ?? 'unpaid') === 'paid';
  return (paid(row.ctf_status) ? CTF_FEE_CENTS : 0)
    + (paid(row.tmf_status) ? TMF_FEE_CENTS : 0);
}

function sumFields(row, keys) {
  return keys.reduce((sum, key) => {
    const value = row[key];
    return sum + (Number.isSafeInteger(value) && value >= 0 ? value : 0);
  }, 0);
}

function dashboardPatchWithTotalExpense(row, patch) {
  const touched = Object.keys(patch);
  const feeMoved = touched.includes('ctf_status') || touched.includes('tmf_status');
  if (!feeMoved && !touched.some((key) => TOTAL_EXPENSE_FIELDS.includes(key)
    || TOTAL_REVENUE_FIELDS.includes(key))) return patch;
  const next = { ...row, ...patch };
  const total = sumFields(next, TOTAL_EXPENSE_FIELDS);
  return {
    ...patch,
    ...(feeMoved ? { pier_pass_cents: pierPassCents(next) } : {}),
    total_expense_cents: total,
    profit_cents: sumFields(next, TOTAL_REVENUE_FIELDS) - total,
  };
}

export function dashboardClientFieldValue(field, value) {
  if (field === 'actual_empty_returned_at') {
    if (value && typeof value === 'object') {
      const date = String(value.date || '').trim();
      const time = String(value.time || '').trim();
      const parsed = new Date(`${date}T${time || '12:00'}`).getTime();
      return Number.isFinite(parsed) ? new Date(parsed) : null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }
  if (!DATE_FIELDS.has(field) || value === null || value === undefined) return value ?? null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

function sameFieldValue(left, right) {
  // null and undefined are one absence - a field never written and a field
  // written as null read the same everywhere.
  //
  // "" is deliberately NOT folded in with them. It was, for an hour on
  // 2026-08-16, to stop "open a blank cell, click away" becoming a write; the
  // cost was that clearing a cell whose STORED value is already absent returned
  // {unchanged} and sent nothing, silently, while the cell went on showing
  // whatever it had been showing. That is indistinguishable from the bug this
  // whole day has been about - "删除后点enter这个信息又回来了" - and a save that
  // decides to do nothing must never be the reason a value reappears. The
  // untouched-blank case is handled where it belongs instead: the editor only
  // commits a blank when it OPENED with something to clear.
  const absent = (value) => value === null || value === undefined;
  if (absent(left) && absent(right)) return true;
  const leftValue = left instanceof Date ? left.getTime() : left;
  const rightValue = right instanceof Date ? right.getTime() : right;
  return Object.is(leftValue, rightValue);
}

export function dashboardAutosaveErrorCode(error) {
  const value = error?.details?.code || error?.code || '';
  return String(value).replace(/^functions\//, '').toUpperCase() || 'SAVE_FAILED';
}

export function createDashboardFieldAutosaveController({
  callUpdate,
  getRow,
  applyPatch,
  onStatus = () => {},
  onError = () => {},
  requestIdFactory = defaultRequestId,
}) {
  if (typeof callUpdate !== 'function' || typeof getRow !== 'function'
    || typeof applyPatch !== 'function' || typeof onStatus !== 'function'
    || typeof onError !== 'function' || typeof requestIdFactory !== 'function') {
    throw new TypeError('Dashboard autosave options are invalid.');
  }
  const queues = new Map();
  let destroyed = false;

  function save({ containerId, field, value }) {
    if (destroyed || typeof containerId !== 'string' || !containerId
      || typeof field !== 'string' || !field) {
      return Promise.reject(new TypeError('Dashboard autosave request is invalid.'));
    }
    const initialRow = getRow(containerId);
    if (!initialRow || typeof initialRow !== 'object') {
      return Promise.reject(new TypeError('Dashboard autosave row is unavailable.'));
    }
    const previousValue = initialRow[field] ?? null;
    const optimisticValue = dashboardClientFieldValue(field, value);
    // A protected note paints nothing ahead of the answer. `value` here is the
    // sentence just typed, not the note, and the note it will become is composed
    // on the server - it carries a stamp with the server's clock in it. Painting
    // the sentence alone would blank the rest of the note for a moment and then
    // flash it back, and comparing that sentence to the note for "unchanged"
    // would swallow the save whenever someone retyped a line already in there.
    const appendOnlyNote = APPEND_ONLY_NOTE_FIELDS.has(field);
    if (!appendOnlyNote) {
      if (sameFieldValue(previousValue, optimisticValue)) return Promise.resolve({ unchanged: true });
      applyPatch(containerId, dashboardPatchWithTotalExpense(initialRow, {
        [field]: optimisticValue,
      }));
    } else if (!String(value ?? '').trim()) {
      return Promise.resolve({ unchanged: true });
    }
    onStatus({ containerId, field, state: 'saving' });
    const requestId = requestIdFactory();
    const prior = queues.get(containerId) || Promise.resolve();
    const operation = prior.catch(() => {}).then(async () => {
      if (destroyed) throw new Error('Dashboard autosave was destroyed.');
      const currentRow = getRow(containerId);
      const expectedRevision = currentRow?.information_revision;
      if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 0) {
        throw new TypeError('Dashboard row revision is invalid.');
      }
      try {
        const response = await callUpdate({
          containerId,
          field,
          value,
          expectedRevision,
          requestId,
          // Hold is sent whole now, and the server replaces it only when told so -
          // a tab from before 2026-09-25 sends an addition and must stay appended.
          ...(field === 'hold_note' ? { noteMode: 'replace' } : {}),
        });
        const data = response?.data;
        if (!data || !Number.isSafeInteger(data.informationRevision)
          || data.informationRevision < 0) {
          throw new TypeError('Dashboard autosave response is invalid.');
        }
        const actualPrecision = field === 'actual_empty_returned_at'
          ? (value?.time ? 'date_time' : 'date') : null;
        applyPatch(containerId, dashboardPatchWithTotalExpense(currentRow, {
          [field]: dashboardClientFieldValue(field, data.value),
          ...(actualPrecision ? { actual_empty_returned_precision: actualPrecision } : {}),
          information_revision: data.informationRevision,
        }));
        onStatus({ containerId, field, state: 'saved' });
        return data;
      } catch (error) {
        const latest = getRow(containerId);
        // Nothing was painted for a protected note, so there is nothing to undo.
        if (!appendOnlyNote && latest && sameFieldValue(latest[field] ?? null, optimisticValue)) {
          applyPatch(containerId, dashboardPatchWithTotalExpense(latest, {
            [field]: previousValue,
          }));
        }
        onStatus({ containerId, field, state: 'error' });
        onError(error, { containerId, field });
        throw error;
      }
    });
    queues.set(containerId, operation);
    operation.finally(() => {
      if (queues.get(containerId) === operation) queues.delete(containerId);
    }).catch(() => {});
    return operation;
  }

  return Object.freeze({
    save,
    destroy() {
      destroyed = true;
      queues.clear();
    },
  });
}
